import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import { Badge, Button } from "@core/components";
import { hasAction } from "@core/permissions";
import { cn } from "@core/utils";
import { isArchived, type Cohort } from "../../_shared";

/** The cohort has six phases; only the first (the appel) exists for now. */
const PHASE_COUNT = 6;

function PhaseBar({ filled }: { filled: number }) {
  return (
    <span aria-hidden className="flex gap-xxs">
      {Array.from({ length: PHASE_COUNT }, (_, index) => (
        <span
          key={index}
          className={cn(
            "h-1 w-7 rounded-full",
            index < filled ? "bg-menthe-600" : "bg-ink-200"
          )}
        />
      ))}
    </span>
  );
}

interface CohortRowProps {
  cohort: Cohort;
  /** Restores an archived cohort (President). */
  onRestore: (cohort: Cohort) => void;
  isRestoring: boolean;
}

/**
 * One cohort of the list (Figma "Cohort Row"): the name with its code over « 20 places | Aucun
 * appel », the phase bar with its status, the number of dossiers and a chevron. An archived cohort
 * reads « Lecture seule » with a grey bar and, for the President, « Restaurer ». The whole row
 * opens the cohort; « Restaurer » sits beside the link so a button never nests in a link.
 *
 * On small screens the phase bar drops out and the status moves under the name.
 */
export function CohortRow({ cohort, onRestore, isRestoring }: CohortRowProps) {
  const { t } = useTranslation("cohorts");
  const archived = isArchived(cohort);
  const appelCount = cohort.rounds?.length;
  const canRestore = archived && hasAction(cohort.available_actions, "restore");

  return (
    <li className="flex items-center gap-sm rounded-lg border border-(--border-default) bg-surface-card pr-md focus-within:ring-2 focus-within:ring-focus-ring">
      <Link
        to="/cohorts/$cohortId"
        params={{ cohortId: cohort.id }}
        className="flex min-w-0 flex-1 items-center gap-md rounded-lg p-md outline-none hover:bg-ink-50"
      >
        <span className="flex min-w-0 flex-1 flex-col gap-xxs">
          <span className="flex min-w-0 flex-wrap items-center gap-xs">
            <span
              className={cn(
                "truncate text-md-semibold",
                archived ? "text-(--text-secondary)" : "text-(--text-primary)"
              )}
            >
              {cohort.name}
            </span>
            {archived ? null : <Badge outlined>{cohort.code}</Badge>}
          </span>
          <span className="text-sm-regular text-(--text-secondary)">
            {archived
              ? t("list.read-only")
              : [
                  t("list.places", { count: cohort.places }),
                  appelCount === undefined
                    ? null
                    : appelCount === 0
                      ? t("list.no-appel")
                      : t("list.appels", { count: appelCount }),
                ]
                  .filter(Boolean)
                  .join(" · ")}
          </span>
        </span>

        <span className="hidden w-56 shrink-0 flex-col gap-xxs md:flex">
          <PhaseBar filled={archived ? 0 : 1} />
          <span className="text-sm-medium text-(--text-primary)">
            {archived ? (
              t("status.archived")
            ) : (
              <>
                {t("phases.appel")}{" "}
                <span className="text-xs-regular text-(--text-secondary)">
                  {t("list.phase-of", { current: 1, total: PHASE_COUNT })}
                </span>
              </>
            )}
          </span>
        </span>

        {archived ? null : (
          <span className="hidden shrink-0 sm:block">
            <Badge>{t("status.preparing")}</Badge>
          </span>
        )}

        <span className="flex shrink-0 flex-col items-center">
          <span className="text-md-medium text-(--text-primary)">
            {cohort.dashboard_counts.submitted}
          </span>
          <span className="text-xs-regular text-(--text-secondary)">
            {t("list.dossiers", { count: cohort.dashboard_counts.submitted })}
          </span>
        </span>

        <Icon
          name="chevron-right"
          size={18}
          className="shrink-0 text-(--text-tertiary)"
        />
      </Link>

      {canRestore ? (
        <Button
          variant="secondary"
          isPending={isRestoring}
          isDisabled={isRestoring}
          onPress={() => {
            onRestore(cohort);
          }}
        >
          <Icon name="archive" size={16} />
          {t("archive.restore")}
        </Button>
      ) : null}
    </li>
  );
}
